import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { BaseWebsocketMessage } from '@qtek/core/websockets-core';
import { AiChat, AiChatEvent } from '@qtek/shared/models';
import { EMPTY, pipe, Subject } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { AiChatService } from './ai-chat.service';
import {
  AiChatDeletePayload,
  AiChatEventFrame,
  AiChatFrame,
  AiChatInsertPayload,
  AiChatQueryPayload,
  AiChatSubscribePayload,
  AiChatUpdatePayload,
} from './ai-chat.types';
import {
  AiChatState,
  aiChatInitialState,
  applyChatCreated,
  applyChatEvents,
  applyChatsQuery,
  applyChatUpdated,
  clearCreatedChat,
  normalizeChat,
  removeChat,
  selectPinned,
  selectRecent,
  withPreviews,
} from './ai-chat.updaters';
import { normalizeChatEvent } from './normalize-chat-event';

// Stable reference so a chat with no events does not look like a change to dependents.
const NO_EVENTS: AiChatEvent[] = [];

export const AiChatStore = signalStore(
  { providedIn: 'root' },
  withState<AiChatState>(aiChatInitialState),
  withProps(() => ({
    chatCreated$: new Subject<{ chatId: string; mysid: string }>(),
  })),
  withComputed(store => {
    const chatsWithPreview = computed(() =>
      withPreviews(store.chats(), store.eventsByChatId())
    );
    return {
      chatsWithPreview,
      pinnedChats: computed(() => selectPinned(chatsWithPreview())),
      recentChats: computed(() => selectRecent(chatsWithPreview())),
    };
  }),
  withMethods((store, service = inject(AiChatService)) => {
    const disconnectChats$ = new Subject<string>();
    const disconnectEvents$ = new Subject<string>();
    const eventRefCounts = new Map<string, number>();
    const eventsMysid = (chatId: string) => `chatEvent_${chatId}`;

    const handleChatsFrame = (msg: BaseWebsocketMessage) => {
      const { op, res, id, mysid } = msg as AiChatFrame;
      switch (op) {
        case 'query': {
          if (!res) return;
          const chats = res
            .map(item => normalizeChat(item))
            .filter((item): item is AiChat => !!item);
          patchState(store, s => applyChatsQuery(s, chats));
          break;
        }
        case 'ins': {
          const normalized =
            normalizeChat(res) ??
            ({ id: id ?? res?.id, title: res?.title } as AiChat);
          if (!normalized.updatedAt) {
            normalized.updatedAt = Date.now();
          }
          patchState(store, s => applyChatCreated(s, normalized, mysid));
          if (mysid) store.chatCreated$.next({ chatId: normalized.id, mysid });
          break;
        }
        case 'upd': {
          const normalized =
            normalizeChat(res) ??
            ({ id: res?.id, title: res?.title } as AiChat);
          // The BE soft-deletes, so a delete reaches other sessions as an `upd` carrying
          // `deletedAt` — treat it as a removal.
          if (normalized.deletedAt) {
            patchState(store, s => removeChat(s, normalized.id));
            return;
          }
          if (!normalized.updatedAt) {
            normalized.updatedAt = Date.now();
          }
          patchState(store, s => applyChatUpdated(s, normalized));
          break;
        }
        // The BE never hard-deletes a Chat today, but a `del` broadcast must still drop the row.
        case 'del': {
          const deletedId = id ?? res?.id;
          if (deletedId) {
            patchState(store, s => removeChat(s, deletedId));
          }
          break;
        }
      }
    };

    const handleEventsFrame = (msg: BaseWebsocketMessage) => {
      const { op, res } = msg as AiChatEventFrame;
      switch (op) {
        case 'query':
          if (!res) return;
          patchState(store, s =>
            applyChatEvents(
              s,
              res
                .map(e => normalizeChatEvent(e))
                .filter((e): e is AiChatEvent => !!e)
            )
          );
          break;
        case 'ins': {
          const event = normalizeChatEvent(res);
          if (event) {
            patchState(store, s => applyChatEvents(s, [event]));
          }
          break;
        }
      }
    };

    const subscribeEvents = rxMethod<AiChatSubscribePayload>(
      pipe(
        mergeMap(payload => {
          // A repeat subscribe on the same mysid replaces the live stream instead of doubling it.
          disconnectEvents$.next(payload.mysid);
          return service.subscribeChatEvents(payload).pipe(
            takeUntil(
              disconnectEvents$.pipe(filter(mysid => mysid === payload.mysid))
            ),
            tap(handleEventsFrame),
            catchError(() => EMPTY)
          );
        })
      )
    );

    return {
      subscribeChats: rxMethod<AiChatSubscribePayload>(
        pipe(
          mergeMap(payload => {
            // A repeat subscribe on the same mysid replaces the live stream instead of doubling it.
            disconnectChats$.next(payload.mysid);
            return service.subscribeChats(payload).pipe(
              takeUntil(
                disconnectChats$.pipe(filter(mysid => mysid === payload.mysid))
              ),
              tap(handleChatsFrame),
              catchError(() => EMPTY)
            );
          })
        )
      ),

      unsubscribeChats(mysid: string): void {
        disconnectChats$.next(mysid);
        patchState(store, s => clearCreatedChat(s, mysid));
      },

      // Ref-counted per chat: several panels can show the same chat, one stream serves them all.
      acquireEvents(chatId: string): void {
        const count = (eventRefCounts.get(chatId) ?? 0) + 1;
        eventRefCounts.set(chatId, count);
        if (count === 1) {
          subscribeEvents({
            mysid: eventsMysid(chatId),
            q: [{ key: 'chatId', op: 'EQ', vals: [chatId] }],
            s: 'model',
            view: 'pln',
          });
        }
      },

      releaseEvents(chatId: string): void {
        const count = (eventRefCounts.get(chatId) ?? 0) - 1;
        if (count > 0) {
          eventRefCounts.set(chatId, count);
          return;
        }
        eventRefCounts.delete(chatId);
        disconnectEvents$.next(eventsMysid(chatId));
      },

      getChats(payload: AiChatQueryPayload): void {
        service.queryChats(payload);
      },

      createChat(payload: AiChatInsertPayload): void {
        service.createChat(payload);
      },

      // Pin/unpin is an update carrying `res: { pinned }`, the same path a rename uses.
      updateChat(payload: AiChatUpdatePayload): void {
        service.updateChat(payload);
      },

      // Removes optimistically: the soft-delete broadcast can come back to nobody when the
      // deleted chat is the one the panel is reading (its subscription goes with it).
      deleteChat(payload: AiChatDeletePayload): void {
        // `id` is `string | number` on the generic WS request; a Chat id is always a UUID.
        patchState(store, s => removeChat(s, String(payload?.id ?? '')));
        service.deleteChat(payload);
      },

      getChatEvents(payload: AiChatQueryPayload): void {
        service.queryChatEvents(payload);
      },

      sendMessage(payload: AiChatInsertPayload): void {
        service.sendEvent(payload);
      },

      resyncStream(payload: {
        chatId: string;
        ts: number;
        lastSeq: number;
      }): void {
        service.resyncStream(payload);
      },

      eventsFor(chatId: string) {
        return computed(() => store.eventsByChatId()[chatId] ?? NO_EVENTS);
      },
    };
  })
);
